import { useEffect, useState, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';

const ann = (e: Entity, key: string) =>
  e.metadata.annotations?.[key] ?? '';

// ---------------------------------------------------------------------------
// Row type
// ---------------------------------------------------------------------------

interface ClusterRow {
  name: string;
  namespace: string;
  entityRef: string;
  instanceName: string;
  location: string;
  lifecycle: string;
  pulsarVersion: string;
  releaseChannel: string;
  clusterProfile: string;
  deploymentType: string;
  serviceUrl: string;
  brokerReplicas: string;
  brokerCpu: string;
  brokerMemory: string;
  brokerAutoscaling: string;
  bookkeeperReplicas: string;
  bookkeeperCpu: string;
  bookkeeperMemory: string;
  bookkeeperLedgerDisk: string;
  bookkeeperJournalDisk: string;
  protocols: string[];
  functionEnabled: string;
  websocketEnabled: string;
  transactionEnabled: string;
}

function toRow(e: Entity): ClusterRow {
  const protocols = ann(e, 'streamnative.io/protocols');
  const namespace = e.metadata.namespace ?? 'default';
  return {
    name: e.metadata.name,
    namespace,
    entityRef: `resource:${namespace}/${e.metadata.name}`,
    instanceName: ann(e, 'streamnative.io/instance-name'),
    location: ann(e, 'streamnative.io/location'),
    lifecycle: (e as any).spec?.lifecycle ?? '',
    pulsarVersion: ann(e, 'streamnative.io/pulsar-version'),
    releaseChannel: ann(e, 'streamnative.io/release-channel'),
    clusterProfile: ann(e, 'streamnative.io/cluster-profile'),
    deploymentType: ann(e, 'streamnative.io/deployment-type'),
    serviceUrl: ann(e, 'streamnative.io/service-url'),
    brokerReplicas: ann(e, 'streamnative.io/broker-replicas'),
    brokerCpu: ann(e, 'streamnative.io/broker-cpu'),
    brokerMemory: ann(e, 'streamnative.io/broker-memory'),
    brokerAutoscaling: ann(e, 'streamnative.io/broker-autoscaling'),
    bookkeeperReplicas: ann(e, 'streamnative.io/bookkeeper-replicas'),
    bookkeeperCpu: ann(e, 'streamnative.io/bookkeeper-cpu'),
    bookkeeperMemory: ann(e, 'streamnative.io/bookkeeper-memory'),
    bookkeeperLedgerDisk: ann(e, 'streamnative.io/bookkeeper-ledger-disk'),
    bookkeeperJournalDisk: ann(e, 'streamnative.io/bookkeeper-journal-disk'),
    protocols: protocols ? protocols.split(',').map(s => s.trim()).filter(Boolean) : [],
    functionEnabled: ann(e, 'streamnative.io/function-enabled'),
    websocketEnabled: ann(e, 'streamnative.io/websocket-enabled'),
    transactionEnabled: ann(e, 'streamnative.io/transaction-enabled'),
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  filterBar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  select: { minWidth: 180 },
  lifecycleProduction: {
    backgroundColor: '#c8e6c9',
    color: '#1b5e20',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  lifecycleStaging: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  lifecycleDevelopment: {
    backgroundColor: '#e3f2fd',
    color: '#0d47a1',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  lifecycleUnknown: {
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  specLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    lineHeight: 1,
  },
  specValue: {
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  specGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, auto)',
    gap: `${theme.spacing(0.25)}px ${theme.spacing(1.5)}px`,
    whiteSpace: 'nowrap',
  },
  featureChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  featureOn: {
    backgroundColor: '#c8e6c9',
    color: '#1b5e20',
  },
  featureOff: {
    backgroundColor: '#f5f5f5',
    color: '#9e9e9e',
  },
  protocolChip: {
    height: 20,
    fontSize: '0.7rem',
    backgroundColor: '#e8eaf6',
    color: '#283593',
  },
}));

function LifecycleChip({ lifecycle, classes }: { lifecycle: string; classes: ReturnType<typeof useStyles> }) {
  const cls =
    lifecycle === 'production'
      ? classes.lifecycleProduction
      : lifecycle === 'staging'
        ? classes.lifecycleStaging
        : lifecycle === 'development'
          ? classes.lifecycleDevelopment
          : classes.lifecycleUnknown;
  return <Chip label={lifecycle} size="small" className={cls} />;
}

// ---------------------------------------------------------------------------
// Component details (rendered in the double-height cells)
// ---------------------------------------------------------------------------

function BrokerCell({ row, classes }: { row: ClusterRow; classes: ReturnType<typeof useStyles> }) {
  return (
    <div className={classes.specGrid}>
      <span className={classes.specLabel}>Replicas</span>
      <span className={classes.specValue}>
        {row.brokerReplicas}
        {row.brokerAutoscaling && (
          <Typography component="span" variant="caption" color="textSecondary">
            {' '}(auto {row.brokerAutoscaling})
          </Typography>
        )}
      </span>
      <span className={classes.specLabel}>CPU</span>
      <span className={classes.specValue}>{row.brokerCpu}</span>
      <span className={classes.specLabel}>Memory</span>
      <span className={classes.specValue}>{row.brokerMemory}</span>
    </div>
  );
}

function BookkeeperCell({ row, classes }: { row: ClusterRow; classes: ReturnType<typeof useStyles> }) {
  return (
    <div className={classes.specGrid}>
      <span className={classes.specLabel}>Replicas</span>
      <span className={classes.specValue}>{row.bookkeeperReplicas}</span>
      <span className={classes.specLabel}>CPU</span>
      <span className={classes.specValue}>{row.bookkeeperCpu}</span>
      <span className={classes.specLabel}>Memory</span>
      <span className={classes.specValue}>{row.bookkeeperMemory}</span>
      <span className={classes.specLabel}>Ledger</span>
      <span className={classes.specValue}>{row.bookkeeperLedgerDisk}</span>
    </div>
  );
}

function FeaturesCell({ row, classes }: { row: ClusterRow; classes: ReturnType<typeof useStyles> }) {
  const features = [
    { label: 'Functions', on: row.functionEnabled === 'true' },
    { label: 'Websocket', on: row.websocketEnabled === 'true' },
    { label: 'Txn', on: row.transactionEnabled === 'true' },
  ];
  return (
    <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
      {row.protocols.map(p => (
        <Chip key={p} label={p} size="small" className={classes.protocolChip} />
      ))}
      {features.map(f => (
        <Chip
          key={f.label}
          label={f.label}
          size="small"
          className={`${classes.featureChip} ${f.on ? classes.featureOn : classes.featureOff}`}
        />
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const StreamNativePage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [rows, setRows] = useState<ClusterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [selectedInstance, setSelectedInstance] = useState('');
  const [selectedLifecycle, setSelectedLifecycle] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'Resource', 'spec.type': 'pulsar-cluster' },
        });
        setRows(items.map(toRow));
      } catch (e: any) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [catalogApi]);

  const instanceOptions = useMemo(
    () => [...new Set(rows.map(r => r.instanceName).filter(Boolean))].sort(),
    [rows],
  );
  const lifecycleOptions = useMemo(
    () => [...new Set(rows.map(r => r.lifecycle).filter(Boolean))].sort(),
    [rows],
  );
  const locationOptions = useMemo(
    () => [...new Set(rows.map(r => r.location).filter(Boolean))].sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    let data = rows;
    if (selectedInstance) data = data.filter(r => r.instanceName === selectedInstance);
    if (selectedLifecycle) data = data.filter(r => r.lifecycle === selectedLifecycle);
    if (selectedLocation) data = data.filter(r => r.location === selectedLocation);
    return data;
  }, [rows, selectedInstance, selectedLifecycle, selectedLocation]);

  const columns: TableColumn<ClusterRow>[] = useMemo(
    () => [
      {
        title: 'Cluster',
        field: 'name',
        render: (row: ClusterRow) => (
          <Box>
            <Link
              to={`/catalog/${encodeURIComponent(row.namespace)}/resource/${encodeURIComponent(row.name)}`}
            >
              {row.name}
            </Link>
            <Typography variant="caption" display="block" color="textSecondary">
              {row.instanceName} &middot; {row.location}
            </Typography>
          </Box>
        ),
      },
      {
        title: 'Lifecycle',
        field: 'lifecycle',
        render: (row: ClusterRow) => (
          <LifecycleChip lifecycle={row.lifecycle} classes={classes} />
        ),
        width: '100px',
      },
      {
        title: 'Version',
        field: 'pulsarVersion',
        render: (row: ClusterRow) => (
          <Box>
            <Typography variant="body2">{row.pulsarVersion}</Typography>
            <Typography variant="caption" color="textSecondary">
              {row.releaseChannel}
            </Typography>
          </Box>
        ),
        width: '110px',
      },
      {
        title: 'Broker',
        sorting: false,
        render: (row: ClusterRow) => <BrokerCell row={row} classes={classes} />,
      },
      {
        title: 'BookKeeper',
        sorting: false,
        render: (row: ClusterRow) => <BookkeeperCell row={row} classes={classes} />,
      },
      {
        title: 'Features',
        sorting: false,
        render: (row: ClusterRow) => <FeaturesCell row={row} classes={classes} />,
        width: '200px',
      },
      {
        title: 'Service URL',
        field: 'serviceUrl',
        render: (row: ClusterRow) =>
          row.serviceUrl ? (
            <Tooltip title={row.serviceUrl}>
              <Typography variant="caption" noWrap style={{ maxWidth: 220, display: 'block' }}>
                {row.serviceUrl}
              </Typography>
            </Tooltip>
          ) : (
            '—'
          ),
      },
    ],
    [classes],
  );

  if (error) return <ResponseErrorPanel error={error} />;

  return (
    <Page themeId="tool">
      <Header title="StreamNative Pulsar Clusters" subtitle="All Pulsar clusters managed via StreamNative" />
      <Content>
        {loading && <Progress />}
        {!loading && (
          <>
            <Box className={classes.filterBar}>
              <FormControl variant="outlined" size="small" className={classes.select}>
                <InputLabel>Instance</InputLabel>
                <Select
                  label="Instance"
                  value={selectedInstance}
                  onChange={e => setSelectedInstance(e.target.value as string)}
                >
                  <MenuItem value="">All</MenuItem>
                  {instanceOptions.map(o => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.select}>
                <InputLabel>Lifecycle</InputLabel>
                <Select
                  label="Lifecycle"
                  value={selectedLifecycle}
                  onChange={e => setSelectedLifecycle(e.target.value as string)}
                >
                  <MenuItem value="">All</MenuItem>
                  {lifecycleOptions.map(o => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.select}>
                <InputLabel>Location</InputLabel>
                <Select
                  label="Location"
                  value={selectedLocation}
                  onChange={e => setSelectedLocation(e.target.value as string)}
                >
                  <MenuItem value="">All</MenuItem>
                  {locationOptions.map(o => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Table<ClusterRow>
              title={`Pulsar Clusters (${filtered.length})`}
              data={filtered}
              columns={columns}
              options={{
                paging: false,
                search: true,
                padding: 'dense',
                rowStyle: { verticalAlign: 'top' },
              }}
            />
          </>
        )}
      </Content>
    </Page>
  );
};
